import { differenceInDays, startOfDay, startOfToday } from 'date-fns';

function last(arr) {
  return arr[arr.length - 1];
}

export function isRoundTrip(itinerary) {
  return itinerary.legs.length === 2;
}

export function selectOutboundDepartureSection(itinerary) {
  return itinerary.legs[0].segments[0].sections[0];
}

export function selectOutboundDestinationSection(itinerary) {
  return last(itinerary.legs[0].segments[0].sections);
}

export function selectInboundDestinationSection(itinerary) {
  return isRoundTrip(itinerary)
    ? last(last(itinerary.legs).segments[0].sections)
    : null;
}

export function selectOfferDuration(offer = {}) {
  const startOfDay = new Date();
  startOfDay.setUTCHours(0, 0, 0, 0);

  return Math.floor(
    (offer.expirationDate - startOfDay.getTime()) / (1000 * 60 * 60 * 24)
  );
}

export function selectTripDuration(itinerary) {
  const outbound = selectOutboundDepartureSection(itinerary);
  const inbound = selectInboundDestinationSection(itinerary);

  if (isRoundTrip(itinerary)) {
    return differenceInDays(
      startOfDay(Date.parse(inbound.arrivalDate)),
      startOfDay(Date.parse(outbound.departureDate))
    );
  }

  return 0;
}

export function selectDaysInAdvance(itinerary) {
  const outbound = selectOutboundDepartureSection(itinerary);

  return differenceInDays(
    startOfDay(Date.parse(outbound.departureDate)),
    startOfToday()
  );
}

export function selectOriginDestinationCodes(itinerary) {
  const origin = itinerary.legs[0].segments[0].sections[0].departure.iata;
  const destination = last(itinerary.legs[0].segments[0].sections).destination
    .iata;

  return `${origin}-${destination}`;
}

export function selectCarrierBrands(itinerary) {
  return itinerary.legs
    .map((leg) => {
      return leg.segments[0].carrier.id;
    })
    .join('-');
}

export function selectPaxType(searchCriteria = {}) {
  return Object.entries(searchCriteria.pax)
    .filter(([, value]) => Boolean(value))
    .reduce((acc, [key]) => {
      if (key.toLowerCase().includes('adult')) {
        return 'ADULT';
      } else if (key.toLowerCase().includes('children')) {
        return 'CHILD';
      } else {
        return 'INFANT';
      }
    }, '');
}

export function selectItineraryDates(itinerary) {
  const firstSection = itinerary.legs[0].segments[0].sections[0];
  const lastSection = last(last(itinerary.legs).segments[0].sections);

  return [firstSection.departureDate, lastSection.arrivalDate];
}

export function selectPercentage(offer) {
  const percentage = Math.round(
    (offer.deposit.amount * 100) / offer.frozenPrimePrice.amount
  );

  return Math.floor(Math.round(percentage / 5) * 5);
}

export function selectItineraryInfo(itinerary) {
  const outboundDeparture = selectOutboundDepartureSection(itinerary);
  const outboundDestination = selectOutboundDestinationSection(itinerary);
  const inboundDestination = selectInboundDestinationSection(itinerary);
  return {
    departure: outboundDeparture.departure.cityName,
    destination: outboundDestination.destination.cityName,
    departureDate: outboundDeparture.departureDate,
    arrivalDate: inboundDestination ? inboundDestination.departureDate : '',
  };
}

export function selectTotalPax(searchCriteria) {
  const { pax } = searchCriteria;
  return pax.numAdults + pax.numChildren + pax.numInfants;
}

export const selectFreeTrialPlusSubscription = (subscriptionOffers = []) => {
  return subscriptionOffers.find(
    (offer) =>
      offer.type === 'PLUS' && offer.subscriptionPeriod === 'FREE_TRIAL'
  );
};

export const selectSubscriptionPeriodInDays = (subscriptionOffer = {}) => {
  const { amount, timeUnit } = subscriptionOffer?.duration || {};

  switch (timeUnit) {
    case 'MONTHS':
      return amount * 30;
    default:
      return amount;
  }
};

export const isYearlyPaidMonthlySubscription = (subscriptionOffer) => {
  return !!subscriptionOffer?.catalogueOffer?.plan.membershipTier.policies.autoRenewalPolicy.deactivationConditions.includes(
    'DEACTIVATION_ALLOWED_AFTER_12_CONSECUTIVE_MONTHLY_RENEWALS'
  );
};

export const selectSubscriptionOfferId = (subscriptionOffer = {}) =>
  subscriptionOffer.offerId;

export const selectSubscriptionPrice = (subscriptionOffer = {}) =>
  subscriptionOffer.renewalPrice;

export const selectPriceFreezeExpirationDate = (data = {}) => {
  return data?.itineraryPriceFreeze?.condition?.expirationDate;
};

export const selectPriceFreezeCondition = (data = {}) =>
  data?.itineraryPriceFreeze?.condition;

export const selectPriceFreezeOfferByType = (offers, type) =>
  offers?.find((offer) => offer?.offer?.offerType === type);
